<script setup>
import { ref, computed, watch, onMounted } from "vue";
import axios from "@/lib/axios";
import Swal from "sweetalert2";
import { DateTime } from "luxon";

const props = defineProps({
    therapist: {
        type: Object,
        required: true,
    },
    location: {
        type: Object,
        required: true,
    },
    mode: {
        type: String,
        default: null, // 'absent', 'booking', 'hold', 'recommendation', 'hart_hours'
    },
    slotDuration: {
        type: [ Number, null ],
        default: null,
    },
    defaultView: {
        type: String,
        default: "week",
    },
    additionalSlotValidation: {
        type: Function,
        default: () => ({ isValid: true }),
    },
    modeEventsOnly: {
        type: [ Boolean, String ],
        default: false,
    },
    virtualEvents: {
        type: Array,
        default: () => [],
    },
    suggestedSlots: {
        type: Array,
        default: () => [],
    },
});

const apiUrl = "/server/api/";

const emit = defineEmits([
    "done",
    "events-loaded",
    "virtual-delete",
    "suggestion-click",
    "view-changed",
]);
const viewTypes = {
    day: "Day",
    week: "Week",
    month: "Month",
};
const modeEventsOnly =
  typeof props.modeEventsOnly === "string" ?
      props.modeEventsOnly === "true" :
      props.modeEventsOnly;
const currentView = ref(props.defaultView);
const currentDate = ref(
    DateTime.now().setZone(props.location?.timezone || "UTC")
);
const isGlobalEvent = ref(false);
const viewDate = ref(currentDate.value);
const events = ref([]);
const isLoading = ref(false);
const loadingMessage = ref("Loading calendar data, please wait...");
const showSuggestions = ref(true);
const showVirtualEvents = ref(true);
const hoverPreview = ref(null);
const pointerSelecting = ref(false);
const pointerSelectionDate = ref(null);
const eventData = Object.freeze({
    absent: {
        label: "Absent",
        color: "bg-red-200 select-pulse",
        forceSlotDuration: false,
        sendToServer: true,
        takesLimit: false,
    },
    hold: {
        label: "Hold",
        color: "bg-yellow-200 select-pulse",
        forceSlotDuration: false,
        sendToServer: true,
        takesLimit: true,
    },
    hart_hours: {
        label: "Hart Hours",
        color: "bg-green-200 select-pulse",
        forceSlotDuration: false,
        sendToServer: true,
        takesLimit: false,
    },
    booking: {
        label: "Booking",
        color: "bg-violet-200 select-pulse",
        forceSlotDuration: true,
        sendToServer: false,
        takesLimit: true,
    },
    recommendation: {
        label: "Recommendation",
        color: "bg-yellow-200 select-pulse",
        forceSlotDuration: true,
        sendToServer: false,
        takesLimit: false,
    },
    virtual: {
        label: "Virtual",
        color: "bg-yellow-200 select-pulse",
        forceSlotDuration: false,
        sendToServer: false,
        takesLimit: false,
    },
    null: {
        label: "Unknown",
        color: "bg-gray-200 select-pulse",
        forceSlotDuration: false,
        sendToServer: false,
        takesLimit: false,
    },
});

if (props.mode !== null && !eventData[props.mode])
    throw new Error("Invalid mode");
if (
    props.mode !== null &&
  props.slotDuration === null &&
  eventData[props.mode].forceSlotDuration
)
    throw new Error("Slot duration is required for this mode");

const processedVirtualEvents = computed(() => {
    if (!showVirtualEvents.value) return [];

    return props.virtualEvents.map((event) => ({
        ...event,
        isVirtual: true,
        time_start:
      typeof event.time_start === "string" ?
          createDateTime(event.time_start) :
          event.time_start,
        time_end:
      typeof event.time_end === "string" ?
          createDateTime(event.time_end) :
          event.time_end,
    }));
});
const processedSuggestedSlots = computed(() => {
    if (!showSuggestions.value) return [];

    return props.suggestedSlots.map((slot) => ({
        ...slot,
        isVirtual: true,
        isSuggestion: true, // Flag to identify suggestions
        time_start:
      typeof slot.start === "string" ? createDateTime(slot.start) : slot.start,
        time_end:
      typeof slot.start === "string" ?
          createDateTime(slot.start).plus({ minutes: props.slotDuration || 60 }) :
          slot.start.plus({ minutes: props.slotDuration || 60 }),
        label: slot.label || "Suggested Slot",
        type: "suggestion",
        deleteable: false,
        collideable: false,
    }));
});
const allVirtualEvents = computed(() => {
    const virtualEvents = processedVirtualEvents.value;
    const suggestedSlots = processedSuggestedSlots.value;
    return [ ...virtualEvents, ...suggestedSlots ];
});
const therapist_id = ref(null);
const location_id = ref(null);
const showSlotDialog = ref(false);
const selectedSlots = ref(null);
const isDragging = ref(false);
const editingEvent = ref(null);
const originalEventData = ref(null);
const isEditing = computed(() => Boolean(editingEvent.value));
const showOtherLocations = ref(true);
const slotValidation = computed(() => {
    if (!selectedSlots.value) return { isValid: true };

    const now = DateTime.now().setZone(props.location?.timezone || "UTC");
    const selectedDate = createDateTime(selectedSlots.value.date);
    const startTime = createDateTime(
        selectedSlots.value.date,
        selectedSlots.value.startTime
    );

    // Past validation
    if (
        startTime < now &&
    [ "booking", "hold", "recommendation" ].includes(props.mode)
    ) {
        return {
            isValid: false,
            errors: [ "Cannot create bookings, recommendations or holds in the past" ],
        };
    }

    // Same day validation
    if (
        selectedDate.hasSame(now, "day") &&
    !props.therapist.accepts_same_day &&
    [ "booking", "hold", "recommendation" ].includes(props.mode)
    ) {
        return {
            isValid: false,
            errors: [ "Therapist doesn't accept same-day bookings" ],
        };
    }

    // Next day validation
    if (
        selectedDate.hasSame(now.plus({ days: 1 }), "day") &&
    !props.therapist.accepts_next_day &&
    [ "booking", "hold", "recommendation" ].includes(props.mode)
    ) {
        return {
            isValid: false,
            errors: [ "Therapist doesn't accept next-day bookings" ],
        };
    }

    // Max sessions validation
    if (eventData[props.mode].takesLimit) {
        const dayEvents = getEventsForDate(selectedDate).filter(
            (e) => eventData[e.type]?.takesLimit
        );
        if (dayEvents.length >= props.therapist.max_sessions) {
            return {
                isValid: false,
                errors: [
                    `Therapist has reached maximum sessions (${props.therapist.max_sessions}) for this day`,
                ],
            };
        }
    }

    const weekStart = selectedDate.startOf("week");
    const weekEnd = selectedDate.endOf("week");

    // Get all events in the current week that count towards the limit
    const weekEvents = events.value.filter(
        (e) =>
            eventData[e.type].takesLimit &&
      e.time_start >= weekStart &&
      e.time_end <= weekEnd
    );

    if (weekEvents.length >= props.therapist.max_sessions_per_week) {
        return {
            isValid: false,
            errors: [
                `Therapist has reached maximum sessions (${props.therapist.max_sessions_per_week}) for this week`,
            ],
        };
    }

    if (
        !props.therapist.offers_after_hours &&
    isOutsideWorkingHours(selectedSlots.value.startTime)
    )
        return { isValid: false, errors: [ "Time slot is outside working hours" ] };

    if (
        !props.therapist.offers_after_hours &&
    isOutsideWorkingHours(selectedSlots.value.endTime)
    )
        return { isValid: false, errors: [ "Time slot is outside working hours" ] };

    const hasCollision = hasEventCollision(
        selectedSlots.value.startTime,
        selectedSlots.value.endTime,
        selectedSlots.value.date,
        editingEvent.value?.ROWID
    );

    if (hasCollision)
        return {
            isValid: false,
            errors: [ "Time slot overlaps with existing event" ],
        };

    // Hart hours duration validation
    if (props.mode === "hart_hours") {
    // Calculate duration in minutes
        const start = createDateTime(
            selectedSlots.value.date,
            selectedSlots.value.startTime
        );
        const end = createDateTime(
            selectedSlots.value.date,
            selectedSlots.value.endTime
        );
        const durationMinutes = end.diff(start, "minutes").minutes;

        if (durationMinutes < props.therapist.session_time)
            return {
                isValid: false,
                ignorable: true,
                errors: [
                    `Hart hours duration (${durationMinutes} min) is less than therapist's session time (${props.therapist.session_time} min)`,
                ],
            };

        // Check for overlap with other hart hours
        const hartHoursOverlap = events.value.some((event) => {
            if (editingEvent.value?.ROWID === event.ROWID) return false;
            if (event.type !== "hart_hours") return false;

            // Check for overlap
            const eventStart = createDateTime(event.time_start);
            const eventEnd = createDateTime(event.time_end);

            return (
                start < eventEnd &&
        end > eventStart &&
        eventStart.hasSame(selectedDate, "day")
            );
        });

        if (hartHoursOverlap)
            return {
                isValid: false,
                ignorable: true,
                errors: [ "This hart hours slot overlaps with existing hart hours" ],
            };
    }

    // Prevent recommendations/bookings too close to bookings/holds
    if (
        [ "booking", "recommendation" ].includes(props.mode) &&
    props.therapist.appointments_gap > 0
    ) {
        const gap = props.therapist.appointments_gap;
        const start = createDateTime(
            selectedSlots.value.date,
            selectedSlots.value.startTime
        );
        const end = createDateTime(
            selectedSlots.value.date,
            selectedSlots.value.endTime
        );

        const closeEvent = events.value.find((event) => {
            if (![ "booking", "hold" ].includes(event.type)) return false;
            // Check if the new slot is within gap minutes before/after this event
            const eventStart = createDateTime(event.time_start);
            const eventEnd = createDateTime(event.time_end);
            return (
                Math.abs(start.diff(eventEnd, "minutes").minutes) <= gap ||
        Math.abs(end.diff(eventStart, "minutes").minutes) <= gap ||
        start <= eventEnd && end >= eventStart // overlaps
            );
        });

        if (closeEvent) {
            return {
                isValid: false,
                ignorable: false,
                errors: [
                    `Recommendation must be at least ${gap} minutes away from bookings/holds`,
                ],
            };
        }
    }

    // Check for hart_hours collision if not creating hart_hours/absent
    if (
        props.mode !== "hart_hours" &&
    props.mode !== "absent" &&
    props.therapist?.uses_hart_hours
    ) {
        const selectedStart = selectedSlots.value.startTime;

        // All hart_hours events for the selected day
        const dayHartEvents = events.value.filter((e) => {
            if (e.type !== "hart_hours") return false;
            if (!e.time_start.hasSame(selectedDate, "day")) return false;

            return isGlobalEventVisible(e, selectedDate);
        });

        // If no hart-hours defined for that day -> treat as outside hart hours (show warning)
        if (dayHartEvents.length === 0) {
            return {
                isValid: false,
                ignorable: true,
                errors: [ "Event is not within the hart hours (no hart hours today)" ],
            };
        }

        // If hart-hours exist, ensure the selected slot is inside at least one hart range
        const insideAnyHart = dayHartEvents.some((e) =>
            isSlotInsideTimeRange(
                selectedStart,
                e.time_start.toFormat("HH:mm"),
                e.time_end.toFormat("HH:mm")
            )
        );

        if (!insideAnyHart) {
            return {
                isValid: false,
                ignorable: true,
                errors: [ "Event is not within the hart hours" ],
            };
        }
    }

    return props.additionalSlotValidation(
        props.therapist,
        props.location,
        createDateTime(selectedSlots.value.date, selectedSlots.value.startTime),
        createDateTime(selectedSlots.value.date, selectedSlots.value.endTime)
    );
});

const isGlobalEventRecord = (event) => {
    return !event.location_id || event.location_id === "_global_";
};

const hasEditChanges = computed(() => {
    if (!isEditing.value || !originalEventData.value || !selectedSlots.value)
        return false;

    const original = originalEventData.value;

    return (
        selectedSlots.value.date !== original.date ||
    selectedSlots.value.startTime !== original.startTime ||
    selectedSlots.value.endTime !== original.endTime ||
    isGlobalEvent.value !== original.isGlobal
    );
});

function findNextAvailableDay(startDate) {
    let checkDate = startDate;
    const now = DateTime.now().setZone(props.location?.timezone || "UTC");

    // Check if date is today and therapist accepts same day
    if (checkDate.hasSame(now, "day")) {
        if (!props.therapist.accepts_same_day)
            checkDate = checkDate.plus({ days: 1 });
    }

    // Check if date is tomorrow and therapist accepts next day
    if (checkDate.hasSame(now.plus({ days: 1 }), "day")) {
        if (!props.therapist.accepts_next_day)
            checkDate = checkDate.plus({ days: 1 });
    }

    let daysCounted = 0;
    while (daysCounted < 30) {
        daysCounted++;

        // Check max sessions
        if (
            getEventsForDate(checkDate).filter((e) => {
                return e.type && eventData[e.type] ?
                    eventData[e.type].takesLimit :
                    false;
            }).length >= props.therapist.max_sessions
        ) {
            checkDate = checkDate.plus({ days: 1 });
            continue;
        }

        // All checks passed
        return checkDate;
    }

    Swal.fire({
        icon: "error",
        title: "No available days",
        text: "No available days found in the next 30 days",
    });
    changeView("month", startDate);
    return null;
}

const dateTimeCache = new Map();
function createDateTime(isoOrDate, timeStr = null) {
    const locationTz = props.location?.timezone || "UTC";

    const cacheKey = `${String(isoOrDate)}|${timeStr ?? ""}|${locationTz}`;

    if (dateTimeCache.has(cacheKey)) {
        return dateTimeCache.get(cacheKey);
    }

    let result;

    if (timeStr) {
        result = DateTime.fromISO(`${isoOrDate}T${timeStr}`, {
            zone: locationTz,
        });
    } else if (typeof isoOrDate === "string" && isoOrDate.includes(" ")) {
        result = DateTime.fromSQL(isoOrDate, {
            zone: "UTC",
        }).setZone(locationTz);
    } else if (isoOrDate instanceof Date) {
        result = DateTime.fromJSDate(isoOrDate, {
            zone: locationTz,
        });
    } else if (isoOrDate instanceof DateTime) {
        result = isoOrDate.setZone(locationTz);
    } else {
        result = DateTime.fromISO(isoOrDate, {
            zone: locationTz,
        });
    }

    dateTimeCache.set(cacheKey, result);

    return result;
}

watch([ () => viewDate.value, () => props.location?.timezone ], () => {
    dateTimeCache.clear();
});

function getEventKey(event, date = null) {
    const prefix = date ? `${date.toISODate()}-` : "";

    if (event.ROWID) return `${prefix}row-${event.ROWID}`;
    if (event.id) return `${prefix}id-${event.id}`;
    if (event.zoho_crm_id) return `${prefix}crm-${event.zoho_crm_id}`;

    return `${prefix}${
        event.type || "event"
    }-${event.time_start?.toISO()}-${event.time_end?.toISO()}`;
}

function totalSlotMinutes(timeStr) {
    const [ hours, minutes ] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
}

function minutesToSlot(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function handleDragStart(slot, date = currentDate.value) {
    if (showSlotDialog.value) return;
    if (props.mode === null) return;

    if (props.slotDuration) {
        const maxStart = 24 * 60 - props.slotDuration;
        const startMinutes = totalSlotMinutes(slot);

        slot = minutesToSlot(Math.min(startMinutes, maxStart));
    }

    const endSlot = minutesToSlot(totalSlotMinutes(slot) + props.slotDuration);
    if (!props.therapist.offers_after_hours) {
        if (isOutsideWorkingHours(slot, date)) return;
        if (props.slotDuration && isOutsideWorkingHours(endSlot, date)) {
            const endOfWorkDay = getEndOfWorkingHoursSlot();
            selectedSlots.value = {
                startTime: minutesToSlot(
                    totalSlotMinutes(endOfWorkDay) - props.slotDuration
                ),
                endTime: endOfWorkDay,
            };
            return;
        }
    }

    if (date) currentDate.value = date;

    // If we have a fixed duration, use hover behavior
    if (props.slotDuration) {
        selectedSlots.value = {
            date: currentDate.value.toISODate(),
            startTime: slot,
            endTime: endSlot,
        };
        return;
    }

    // Otherwise use existing drag behavior
    isDragging.value = true;
    selectedSlots.value = {
        date: currentDate.value.toISODate(),
        startTime: slot,
        endTime: slot,
    };
}

function handleDragEnd() {
    if (showSlotDialog.value) return;
    if (!selectedSlots.value) return;

    if (props.slotDuration) {
        selectedSlots.value = {
            date: currentDate.value.toISODate(),
            startTime: padTimeComponent(selectedSlots.value.startTime),
            endTime: padTimeComponent(selectedSlots.value.endTime),
        };
        isGlobalEvent.value = [ "absent", "hart_hours" ].includes(props.mode);
        originalEventData.value = null;
        showSlotDialog.value = true;
        return;
    }

    // Use existing drag end behavior
    if (!isDragging.value) return;
    isDragging.value = false;
    selectedSlots.value = {
        date: currentDate.value.toISODate(),
        startTime: padTimeComponent(selectedSlots.value.startTime),
        endTime: padTimeComponent(selectedSlots.value.endTime),
    };
    isGlobalEvent.value = [ "absent", "hart_hours" ].includes(props.mode);
    originalEventData.value = null;
    showSlotDialog.value = true;
}

// Helper function to pad time components
function padTimeComponent(timeStr) {
    let [ hours, minutes ] = timeStr.split(":").map(Number);
    if (minutes === 60) {
        hours += 1;
        minutes = 0;
    }
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
    )}`;
}

async function handleSave() {
    if (slotValidation.value.ignorable) {
        const proceed = await Swal.fire({
            title: `Are you sure you want to create event '${
                editingEvent.value?.title ?? eventData[props.mode].label
            }' anyway?`,
            text: `Some validation rules are violated, but you can still proceed if you wish.\n\nRule: ${slotValidation.value.errors[0]}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, create it!",
            cancelButtonText: "Cancel",
        }).then((result) => result.isConfirmed);

        if (!proceed) return;
    }

    if (isEditing.value && !hasEditChanges.value) return;

    await eventUpsert();

    showSlotDialog.value = false;
    selectedSlots.value = null;
    editingEvent.value = null;
    originalEventData.value = null;
    hoverPreview.value = null;
}

const parseWorkHoursStr = (timeStr) => {
    if (!timeStr) return { hours: null, minutes: null };
    const [ time, meridiem ] = timeStr.split(" ");
    let [ hours, minutes = 0 ] = time.split(":").map(Number);
    if (meridiem === "PM" && hours !== 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;
    return { hours: hours, minutes: minutes };
};

// Parse working hours
const workHours = props?.location?.days ?
    [
        ...[
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
        ].map((day, i) => {
            const start = parseWorkHoursStr(props.location.days[(i + 1) % 7].start);
            const end = parseWorkHoursStr(props.location.days[(i + 1) % 7].end);
            // If either start or end is missing, treat as no working hours
            if (start.hours === null || end.hours === null) {
                return {
                    day: day,
                    start: { hours: null, minutes: null },
                    end: { hours: null, minutes: null },
                };
            }
            return { day: day, start: start, end: end };
        }),
    ] :
    [];

const currentDateWorkhours = computed(() => {
    return workHours[currentDate.value.weekday - 1];
});

function isGlobalHartHoursVisible(event, date) {
    if (props.mode === "hart_hours") return true;
    const dayHours = workHours[date.weekday - 1];

    if (!dayHours || dayHours.start.hours === null || dayHours.end.hours === null)
        return false;

    const workStart = createDateTime(date).set({
        hour: dayHours.start.hours,
        minute: dayHours.start.minutes ?? 0,
        second: 0,
        millisecond: 0,
    });

    const workEnd = createDateTime(date).set({
        hour: dayHours.end.hours,
        minute: dayHours.end.minutes ?? 0,
        second: 0,
        millisecond: 0,
    });

    // Global Hart Hours are visible only if they overlap
    // the location's working hours.
    return event.time_start < workEnd && event.time_end > workStart;
}

function getHartSlots(date) {
    if (props.mode === "hart_hours") return [];

    const dayStart = createDateTime(date).startOf("day");
    const dayEnd = dayStart.endOf("day");

    return events.value
        .filter((event) => {
            if (event.type !== "hart_hours") return false;

            // Event must occur on this date.
            if (event.time_start > dayEnd || event.time_end < dayStart) return false;

            // Local Hart Hours are always allowed to display.
            if (!isGlobalEventRecord(event)) return true;

            // Global Hart Hours must intersect the location's work hours.
            return isGlobalHartHoursVisible(event, date);
        })
        .map((event) => ({
            start: event.time_start.hour + event.time_start.minute / 60,
            end: event.time_end.hour + event.time_end.minute / 60,
        }));
}

function formatTime(hour, minute = 0) {
    // Create a base DateTime and set the time
    return DateTime.now()
        .setZone(props.location?.timezone || "UTC")
        .set({ hour: hour, minute: minute })
        .toLocaleString(DateTime.TIME_SIMPLE);
}

// Hidden ranges to collapse (00:00-07:00 and 22:00-24:00)
const HIDDEN_RANGES = {
    early: { id: "early", start: 0, end: 7, label: "Early hours" },
    late: { id: "late", start: 22, end: 24, label: "Late hours" },
};

// Track which ranges are open per-date (key: dateISO + '_' + rangeId)
const collapsedOpen = ref({});

function isRangeOpen(rangeId) {
    return Boolean(collapsedOpen.value[rangeId]);
}

function toggleRange(rangeId) {
    collapsedOpen.value[rangeId] = !collapsedOpen.value[rangeId];
}

function countEventsInRange(date, range) {
    const startDT = createDateTime(date).set({
        hour: range.start,
        minute: 0,
        second: 0,
    });
    // use end at the very end of previous minute if end===24
    const endHour = range.end === 24 ? 23 : range.end;
    const endDT = createDateTime(date).set({
        hour: endHour,
        minute: range.end === 24 ? 59 : 0,
        second: 59,
    });

    return getEventsForDate(date).filter((event) => {
        return (
            event.type !== "hart_hours" &&
      event.time_start < endDT &&
      event.time_end > startDT &&
      !event.isVirtual
        );
    }).length;
}

// Return an ordered list of segments that are actually visible in the UI for a given date.
function getSegments() {
    const segments = [];
    let hour = 0;
    while (hour < 24) {
        // eslint-disable-next-line no-loop-func
        const hidden = Object.values(HIDDEN_RANGES).find((r) => r.start === hour);
        if (hidden) {
            if (isRangeOpen(hidden.id)) {
                // range is expanded -> include every hour in the range
                for (let h = hidden.start; h < hidden.end; h++) {
                    segments.push({ type: "hour", hour: h });
                }
            }
            hour = hidden.end;
            continue;
        }
        segments.push({ type: "hour", hour: hour });
        hour += 1;
    }
    return segments;
}

const visibleSegments = computed(() => {
    return getSegments();
});

// Compute top offset (px) for a given DateTime on a rendered day column
function renderedTopFor(dt) {
    const segments = visibleSegments.value;
    const targetHour = dt.hour;
    const targetMin = dt.minute;
    let acc = 0;

    for (const seg of segments) {
        if (seg.hour < targetHour) {
            acc += 60;
            continue;
        }
        if (seg.hour === targetHour) {
            acc += targetMin;
            return acc;
        }
        return acc;
    }

    return acc + targetMin;
}

function renderedHeightFor(startDT, endDT) {
    const topStart = renderedTopFor(startDT);
    const topEnd = renderedTopFor(endDT);
    return Math.max(5, topEnd - topStart);
}

function getWeekDates() {
    const startOfWeek = viewDate.value.startOf("week");
    const dates = [];

    // Generate 7 days from start of week
    for (let i = 0; i < 7; i++) {
        dates.push(startOfWeek.plus({ days: i }));
    }

    return dates;
}

function getMonthDates() {
    const date = viewDate.value;
    const firstDay = date.startOf("month");
    const lastDay = date.endOf("month");
    const dates = [];

    // Previous month days
    const firstDayOfWeek = firstDay.weekday % 7; // Convert Monday=1 to Sunday=0 format
    for (let i = firstDayOfWeek; i > 0; i--) {
        dates.push(firstDay.minus({ days: i }));
    }

    // Current month days
    let curDate = firstDay;
    while (curDate <= lastDay) {
        dates.push(curDate);
        curDate = curDate.plus({ days: 1 });
    }

    // Next month days
    const lastDayOfWeek = lastDay.weekday % 7;
    for (let i = 1; i < 7 - lastDayOfWeek; i++) {
        dates.push(lastDay.plus({ days: i }));
    }

    return dates;
}

function changeView(view, date = null) {
    if (view === currentView.value) return;
    selectedSlots.value = null;
    showSlotDialog.value = false;
    editingEvent.value = null;
    currentView.value = view;
    hoverPreview.value = null;
    if (date) {
        if (date instanceof DateTime) viewDate.value = date;
        else
            viewDate.value = DateTime.fromJSDate(new Date(date)).setZone(
                props.location?.timezone || "UTC"
            );
        currentDate.value = viewDate.value;
    }
    emit("view-changed", view, viewDate.value, events.value);
}

function navigateDate(direction) {
    switch (currentView.value) {
    case "day":
        viewDate.value = viewDate.value.plus({ days: direction });
        currentDate.value = viewDate.value;
        break;
    case "week":
        viewDate.value = viewDate.value.plus({ weeks: direction });
        if (!currentDate.value.hasSame(viewDate.value, "week")) {
            currentDate.value = viewDate.value;
        }
        break;
    case "month":
        viewDate.value = viewDate.value.plus({ months: direction });
        if (!currentDate.value.hasSame(viewDate.value, "month")) {
            currentDate.value = viewDate.value;
        }
        break;
    default:
        console.error("Invalid view type:", currentView.value);
    }
}

async function handleSlotClick(date, event = null) {
    // If not in day view, switch to day view of clicked date
    if (!event) {
        if (currentView.value !== "day") {
            changeView("day", date);
            return;
        }
        return;
    }

    editingEvent.value = event;

    const isGlobal = isGlobalEventRecord(event);
    isGlobalEvent.value = isGlobal;

    selectedSlots.value = {
        date: event.time_start.toISODate(),
        startTime: event.time_start.toFormat("HH:mm"),
        endTime: event.time_end.toFormat("HH:mm"),
    };

    originalEventData.value = {
        date: selectedSlots.value.date,
        startTime: selectedSlots.value.startTime,
        endTime: selectedSlots.value.endTime,
        isGlobal: isGlobal,
    };

    showSlotDialog.value = true;
}

function formatDate(date, format = "full") {
    if (!(date instanceof DateTime)) {
        date = createDateTime(date);
    }

    return date.toLocaleString(
        format === "full" ?
            { ...DateTime.DATE_FULL, weekday: "long" } :
            { ...DateTime.DATE_SHORT, weekday: "short" }
    );
}

async function fetchEvents() {
    if (!props.therapist) return;
    if (!props.location) return;
    if (!therapist_id.value) return;
    if (!location_id.value) return;
    isLoading.value = true;

    try {
        const locationTz = props.location?.timezone || "UTC";
        const start = viewDate.value
            .setZone(locationTz)
            .startOf("month")
            .minus({ days: 7 });
        const end = viewDate.value
            .setZone(locationTz)
            .endOf("month")
            .plus({ days: 7 });

        const params = {
            therapist_id: therapist_id.value,
            from: start.setZone("UTC").toFormat("yyyy-MM-dd HH:mm:ss"),
            to: end.setZone("UTC").toFormat("yyyy-MM-dd HH:mm:ss"),
            all_locations: true,
        };

        // Add type filter if modeEventsOnly is true
        if (modeEventsOnly && props.mode) params.types = props.mode;

        const response = await axios.get(`${apiUrl}events`, { params: params });

        events.value = response.data.data.map((event) => ({
            ...event,
            time_start: createDateTime(event.time_start),
            time_end: createDateTime(event.time_end),
            _isOtherLocation:
        event.location_id !== location_id.value && !isGlobalEventRecord(event),
            _isGlobal: isGlobalEventRecord(event),
        }));

        // Remove hart hours
        events.value = events.value.filter((event) => {
            if (event._isGlobal) return true;
            if (
                event._isOtherLocation &&
        event.type === "hart_hours" &&
        Boolean(event.location_id)
            )
                return false;
            return true;
        });

        emit("events-loaded", events.value);
    } catch (error) {
        console.error("Failed to fetch events:", new Error(error));
    } finally {
        isLoading.value = false;
    }
}

async function eventUpsert() {
    isLoading.value = true;

    const data = {
        date: selectedSlots.value.date,
    };

    if (isEditing.value) {
        data.type = editingEvent.value.type;
        data.ROWID = editingEvent.value.ROWID;
        data.meta = editingEvent.value.meta;
        data.zoho_crm_id = editingEvent.value.zoho_crm_id;
    } else {
        data.type = props.mode;
        data.ROWID = null;
        data.meta = {};
        data.zoho_crm_id = null;
    }

    try {
        const startLocal = createDateTime(data.date, selectedSlots.value.startTime);
        const endLocal = createDateTime(data.date, selectedSlots.value.endTime);

        let axiosResponse = null;
        if (eventData[data.type].sendToServer) {
            let locationId = location_id.value;
            if (isGlobalEvent.value) locationId = "_global_";
            else if (editingEvent.value) locationId = editingEvent.value.location_id;
            axiosResponse = await axios.post(`${apiUrl}event`, {
                therapist_id: therapist_id.value,
                location_id: locationId,
                type: data.type,
                time_start: startLocal.setZone("UTC").toFormat("yyyy-MM-dd HH:mm:ss"),
                time_end: endLocal.setZone("UTC").toFormat("yyyy-MM-dd HH:mm:ss"),
                meta: data.meta,
                zoho_crm_id: data.zoho_crm_id,
                ...data.ROWID ? { ROWID: data.ROWID } : {},
            });
        }

        if (!isEditing.value) {
            emit("done", {
                request: {
                    therapist_id: therapist_id.value,
                    location_id: location_id.value,
                    type: data.type,
                    time_start: startLocal.setZone("UTC").toFormat("yyyy-MM-dd HH:mm:ss"),
                    time_end: endLocal.setZone("UTC").toFormat("yyyy-MM-dd HH:mm:ss"),
                    meta: data.meta,
                    zoho_crm_id: data.zoho_crm_id,
                    ...data.ROWID ? { ROWID: data.ROWID } : {},
                },
                response: axiosResponse,
            });
        }

        fetchEvents();
    } catch (error) {
        console.error("Failed to create time slot:", new Error(error));
        await Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong. Please, try again, or contact support.",
        });
    }

    isLoading.value = false;
}

async function handleDelete() {
    if (!isEditing.value) throw new Error("No event to delete");
    try {
        isLoading.value = true;
        await axios.delete(`${apiUrl}event`, {
            params: {
                ROWID: editingEvent.value.ROWID,
            },
        });

        await fetchEvents();

        await Swal.fire({
            title: "Deleted!",
            text: "The event has been deleted.",
            icon: "success",
            timer: 2000,
        });
    } catch (error) {
        console.error("Failed to delete event:", new Error(error));
        await Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to delete event",
        });
    }

    isLoading.value = false;
    showSlotDialog.value = false;
    selectedSlots.value = null;
    editingEvent.value = null;
    originalEventData.value = null;
    isGlobalEvent.value = false;
    hoverPreview.value = null;
}

function isGlobalEventVisible(event, date = currentDate.value) {
    if (!isGlobalEventRecord(event)) return true;
    if (event.type === "absent") return true;
    if (event.type === "hart_hours") return isGlobalHartHoursVisible(event, date);
    return true;
}

function getEventsForDate(date) {
    const startOfDay = createDateTime(date).startOf("day");
    const endOfDay = startOfDay.endOf("day");

    const regularEvents = events.value.filter((event) => {
        if (!(event.time_start <= endOfDay && event.time_end >= startOfDay))
            return false;

        // Global events bypass location checkbox
        if (isGlobalEventRecord(event)) return isGlobalEventVisible(event, date);

        return event._isOtherLocation ? showOtherLocations.value : true;
    });

    const virtualEventsForDate = allVirtualEvents.value.filter(
        (event) => event.time_start <= endOfDay && event.time_end >= startOfDay
    );

    return [ ...regularEvents, ...virtualEventsForDate ];
}

async function handleCollisionClick() {
    const collision = events.value.find(() =>
        hasEventCollision(
            selectedSlots.value.startTime,
            selectedSlots.value.endTime,
            selectedSlots.value.date,
            editingEvent.value?.ROWID
        )
    );

    if (collision) {
        await handleSlotClick(collision.time_start, collision);
    }
}

function isOutsideWorkingHours(timeStr, date = currentDate.value) {
    const dateWorkhours = workHours[date.weekday - 1];
    if (dateWorkhours.start.hours === null || dateWorkhours.end.hours === null) {
        return true; // Not a working day
    }

    const [ hours, minutes ] = timeStr.split(":").map(Number);
    const time = DateTime.now()
        .setZone(props.location?.timezone || "UTC")
        .set({ hour: hours, minute: minutes });

    const workStart = time.set({
        hour: dateWorkhours.start.hours,
        minute: dateWorkhours.start.minutes || 0,
    });
    const workEnd = time.set({
        hour: dateWorkhours.end.hours,
        minute: dateWorkhours.end.minutes || 0,
    });

    return time < workStart || time > workEnd;
}

function getEndOfWorkingHoursSlot(date = currentDate.value) {
    const dayWorkHours = workHours[date.weekday - 1];
    if (dayWorkHours.end.hours === null) return null;

    return `${String(dayWorkHours.end.hours).padStart(2, "0")}:${String(
        dayWorkHours.end.minutes || 0
    ).padStart(2, "0")}`;
}

function hasEventCollision(startTime, endTime, date, skipEventId = null) {
    const checkStart = createDateTime(date, startTime);
    const checkEnd = createDateTime(date, endTime);

    return events.value.some((event) => {
        if (skipEventId && event.ROWID === skipEventId) return false;
        if (event.type === "hart_hours" || event.isVirtual) return false;

        return checkStart <= event.time_end && checkEnd >= event.time_start;
    });
}

// Helper to check if slot is within time range
function isSlotInsideTimeRange(slot, startTime, endTime) {
    const [ slotHour, slotMin ] = slot.split(":").map(Number);
    const slotTime = DateTime.now()
        .setZone(props.location?.timezone || "UTC")
        .set({ hour: slotHour, minute: slotMin });

    const start = DateTime.now()
        .setZone(props.location?.timezone || "UTC")
        .set({
            hour: Number(startTime.split(":")[0]),
            minute: Number(startTime.split(":")[1]),
        });

    const end = DateTime.now()
        .setZone(props.location?.timezone || "UTC")
        .set({
            hour: Number(endTime.split(":")[0]),
            minute: Number(endTime.split(":")[1]),
        });

    return slotTime >= start && slotTime <= end;
}

function pointerToCalendarTime(event, date) {
    const rect = event.currentTarget.getBoundingClientRect();
    let y = event.clientY - rect.top;
    const calendarHeight = visibleSegments.value.length * 60;

    y = Math.max(0, Math.min(y, calendarHeight - 1));

    const segmentIndex = Math.floor(y / 60);
    const segment = visibleSegments.value[segmentIndex];

    if (!segment || segment.type !== "hour") return null;

    const minuteInsideHour = y - segmentIndex * 60;
    let minute = Math.floor(minuteInsideHour / 5) * 5;
    minute = Math.max(0, Math.min(55, minute));

    const slot = `${String(segment.hour).padStart(2, "0")}:${String(
        minute
    ).padStart(2, "0")}`;

    return {
        date: date,
        slot: slot,
        hour: segment.hour,
        minute: minute,
    };
}

function buildPreview(date, startTime, endTime) {
    const startDT = createDateTime(date.toISODate(), startTime);
    const endDT = createDateTime(date.toISODate(), endTime);

    const top = renderedTopFor(startDT);
    const height = renderedHeightFor(startDT, endDT);

    return {
        date: date.toISODate(),
        startTime: startTime,
        endTime: endTime,
        top: top,
        height: height,
    };
}

function handleCalendarPointerDown(event, date) {
    if (showSlotDialog.value) return;
    if (props.mode === null) return;

    if (event.target.closest("[data-calendar-event]")) return;

    const point = pointerToCalendarTime(event, date);
    if (!point) return;

    currentDate.value = date;

    if (props.slotDuration) {
        let preview = hoverPreview.value;
        if (!preview || preview.date !== date.toISODate()) {
            handleCalendarHover(event, date);
            preview = hoverPreview.value;
        }

        if (!preview) return;

        selectedSlots.value = {
            date: preview.date,
            startTime: preview.startTime,
            endTime: preview.endTime,
        };

        isGlobalEvent.value = [ "absent", "hart_hours" ].includes(props.mode);

        editingEvent.value = null;
        originalEventData.value = null;

        // hoverPreview.value = null;
        showSlotDialog.value = true;

        return;
    }

    pointerSelecting.value = true;
    pointerSelectionDate.value = date;

    selectedSlots.value = {
        date: date.toISODate(),
        startTime: point.slot,
        endTime: point.slot,
    };

    hoverPreview.value = buildPreview(
        date,
        point.slot,
        minutesToSlot(Math.min(totalSlotMinutes(point.slot) + 5, 24 * 60 - 1))
    );

    if (event.currentTarget.setPointerCapture) {
        try {
            event.currentTarget.setPointerCapture(event.pointerId);
        } catch (e) {
            // Safe to ignore.
        }
    }
}

function handleCalendarPointerMove(event, date) {
    if (props.slotDuration) {
        handleCalendarHover(event, date);
        return;
    }

    if (!pointerSelecting.value) return;
    if (!selectedSlots.value) return;

    if (
        pointerSelectionDate.value &&
    !pointerSelectionDate.value.hasSame(date, "day")
    )
        return;

    const point = pointerToCalendarTime(event, date);
    if (!point) return;

    const startMinutes = totalSlotMinutes(selectedSlots.value.startTime);
    let endMinutes = totalSlotMinutes(point.slot);

    if (endMinutes <= startMinutes) endMinutes = startMinutes + 5;
    endMinutes = Math.min(endMinutes, 24 * 60 - 1);
    const endTime = minutesToSlot(endMinutes);

    if (
        !props.therapist.offers_after_hours &&
    isOutsideWorkingHours(endTime, date)
    )
        return;

    if (
        hasEventCollision(selectedSlots.value.startTime, endTime, date.toISODate())
    )
        return;

    selectedSlots.value = {
        ...selectedSlots.value,
        endTime: endTime,
    };

    hoverPreview.value = buildPreview(
        date,
        selectedSlots.value.startTime,
        endTime
    );
}

function handleCalendarPointerUp(event, date) {
    if (props.slotDuration) return;
    if (!pointerSelecting.value) return;
    if (!selectedSlots.value) return;

    pointerSelecting.value = false;
    pointerSelectionDate.value = null;

    selectedSlots.value = {
        date: date.toISODate(),
        startTime: padTimeComponent(selectedSlots.value.startTime),
        endTime: padTimeComponent(selectedSlots.value.endTime),
    };

    isGlobalEvent.value = [ "absent", "hart_hours" ].includes(props.mode);

    originalEventData.value = null;
    editingEvent.value = null;

    showSlotDialog.value = true;

    if (event.currentTarget.releasePointerCapture) {
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        } catch (e) {
            // Safe to ignore.
        }
    }
}

function hourBackgroundClass(hour, date) {
    return slotStyle(hour, 0, date);
}

function previewClass() {
    if (!props.mode) return "";

    return eventData[props.mode]?.color || "bg-blue-200";
}

function previewStyleForDate(date) {
    if (!hoverPreview.value) return { display: "none" };
    if (hoverPreview.value.date !== date.toISODate()) return { display: "none" };
    return {
        top: `${hoverPreview.value.top}px`,
        height: `${Math.max(5, hoverPreview.value.height)}px`,
    };
}

watch([ () => viewDate.value, () => currentView.value ], () => {
    selectedSlots.value = null;

    hoverPreview.value = null;
    pointerSelecting.value = false;
    pointerSelectionDate.value = null;

    fetchEvents();
});

function handleCalendarHover(event, date) {
    if (showSlotDialog.value || props.mode === null || !props.slotDuration)
        return;

    if (event.target.closest("[data-calendar-event]")) {
        if (hoverPreview.value) hoverPreview.value = null;
        return;
    }

    const point = pointerToCalendarTime(event, date);

    if (!point) {
        if (hoverPreview.value) hoverPreview.value = null;
        return;
    }

    const dateISO = date.toISODate();
    let startMinutes = totalSlotMinutes(point.slot);
    startMinutes = Math.min(startMinutes, 1440 - props.slotDuration);

    const startTime = minutesToSlot(startMinutes);
    if (
        hoverPreview.value?.date === dateISO &&
    hoverPreview.value?.startTime === startTime
    )
        return;

    const endTime = minutesToSlot(startMinutes + props.slotDuration);

    hoverPreview.value = buildPreview(date, startTime, endTime);
}

function handleEventPointerEnter() {
    if (hoverPreview.value) hoverPreview.value = null;
}

function handleCalendarLeave() {
    if (!pointerSelecting.value && !showSlotDialog.value) hoverPreview.value = null;
}

function getEventStyle(event) {
    return getWeekEventStyle(event, viewDate.value);
}

function getWeekEventStyle(event, curDate) {
    const dayStart = createDateTime(curDate).startOf("day");
    const dayEnd = createDateTime(curDate).endOf("day");

    let startTime = event.time_start < dayStart ? dayStart : event.time_start;
    let endTime = event.time_end > dayEnd ? dayEnd : event.time_end;
    if (endTime <= dayStart || startTime >= dayEnd) return { display: "none" };

    for (const range of Object.values(HIDDEN_RANGES)) {
        if (isRangeOpen(range.id)) continue;

        const rangeStart = dayStart.set({
            hour: range.start,
            minute: 0,
            second: 0,
            millisecond: 0,
        });

        const rangeEnd =
      range.end === 24 ?
          dayEnd :
          dayStart.set({
              hour: range.end,
              minute: 0,
              second: 0,
              millisecond: 0,
          });

        if (startTime >= rangeStart && endTime <= rangeEnd)
            return { display: "none" };
        if (startTime >= rangeStart && startTime < rangeEnd && endTime > rangeEnd)
            startTime = rangeEnd;
        if (startTime < rangeStart && endTime > rangeStart && endTime <= rangeEnd)
            endTime = rangeStart;
    }

    if (endTime <= startTime) return { display: "none" };

    const top = renderedTopFor(startTime);
    const height = renderedHeightFor(startTime, endTime);
    const maxHeight = visibleSegments.value.length * 60;

    if (top >= maxHeight) return { display: "none" };

    return {
        top: `${Math.max(0, top)}px`,
        height: `${Math.max(5, Math.min(height, maxHeight - Math.max(0, top)))}px`,
        left: "2px",
        right: "2px",
        position: "absolute",
        overflow: "hidden",
    };
}

function getEventLabel(event) {
    if (event.meta?.label) return event.meta.label;
    return eventData[event.type]?.label ?? eventData.null.label;
}

function slotStyle(hour, minute, date = currentDate.value) {
    const time = hour + minute / 60;

    if (
        [ "booking", "recommendation" ].includes(props.mode) &&
    props.therapist.appointments_gap > 0 &&
    events.value.some((event) => {
        let eventStart = createDateTime(event.time_start);
        let eventEnd = createDateTime(event.time_end);
        if (
            ![ "booking", "hold" ].includes(event.type) ||
        eventStart.get("day") !== date.day
        )
            return false;
        eventStart = eventStart.get("hour") + eventStart.get("minute") / 60;
        eventEnd = eventEnd.get("hour") + eventEnd.get("minute") / 60;
        const gap = props.therapist.appointments_gap / 60;

        return (
            (Math.abs(time - eventEnd) < gap ||
          Math.abs(time - eventStart) < gap) &&
        !(
            time >= eventStart.hour + eventStart.minute / 60 &&
          time <= eventEnd.hour + eventEnd.minute / 60
        )
        );
    })
    )
        return "bg-red-50 cursor-no-drop";

    // Hart hours
    if (getHartSlots(date).some((slot) => time >= slot.start && time < slot.end))
        return "bg-green-200";

    // Regular working hours with minute precision
    const dayWorkHours = workHours[date.weekday - 1];
    if (dayWorkHours.start.hours !== null && dayWorkHours.end.hours !== null) {
        const startTime =
      dayWorkHours.start.hours + (dayWorkHours.start.minutes || 0) / 60;
        const endTime =
      dayWorkHours.end.hours + (dayWorkHours.end.minutes || 0) / 60;

        if (time >= startTime && time < endTime) return "bg-blue-50";
    }

    return "";
}

function isToday(date) {
    const now = DateTime.now().setZone(props.location?.timezone || "UTC");
    if (!(date instanceof DateTime)) {
        date = createDateTime(date);
    }

    return date.hasSame(now, "day");
}

function isPast(date) {
    const locationTz = props.location?.timezone || "UTC";
    const now = new Date().toLocaleString("en-US", { timeZone: locationTz });
    const nowDate = new Date(now);

    const checkDate = new Date(
        date.toLocaleString("en-US", { timeZone: locationTz })
    );

    return checkDate < nowDate;
}

function snapTimeToGrid(hours, minutes) {
    if (minutes >= 58) {
        return {
            hours: (hours + 1) % 24,
            minutes: 0,
        };
    }
    return {
        hours: hours,
        minutes: Math.round(minutes / 5) * 5,
    };
}

function handleStartTimeChange(event) {
    const [ dateStr, timeStr ] = event.target.value.split("T");
    const [ hours, minutes ] = timeStr.split(":").map(Number);

    // Snap to 5-minute grid
    const { hours: snapHours, minutes: snapMinutes } = snapTimeToGrid(
        hours,
        minutes
    );
    const newTime = `${String(snapHours).padStart(2, "0")}:${String(
        snapMinutes
    ).padStart(2, "0")}`;

    // Update date if changed
    if (dateStr !== selectedSlots.value.date) selectedSlots.value.date = dateStr;

    if (!props.therapist.offers_after_hours) {
        const dt = DateTime.fromISO(`${dateStr}T${newTime}`, {
            zone: props.location?.timezone || "UTC",
        });

        const workStart = dt.set({
            hour: currentDateWorkhours.value.start,
            minute: 0,
        });
        const workEnd = dt.set({ hour: currentDateWorkhours.value.end, minute: 0 });

        if (dt < workStart) {
            selectedSlots.value.startTime = workStart.toFormat("HH:mm");
            return;
        }
        if (dt > workEnd) {
            selectedSlots.value.startTime = workEnd.toFormat("HH:mm");
            return;
        }
    }

    selectedSlots.value.startTime = newTime;
    if (props.slotDuration) {
    // Set endTime
        const endTime = DateTime.fromISO(`${selectedSlots.value.date}T${newTime}`, {
            zone: props.location?.timezone || "UTC",
        }).plus({ minutes: props.slotDuration });
        selectedSlots.value.endTime = endTime.toFormat("HH:mm");
    }
}

function handleEndTimeChange(event) {
    const [ dateStr, timeStr ] = event.target.value.split("T");
    const [ hours, minutes ] = timeStr.split(":").map(Number);

    // Snap to 5-minute grid
    const { hours: snapHours, minutes: snapMinutes } = snapTimeToGrid(
        hours,
        minutes
    );
    const newTime = `${String(snapHours).padStart(2, "0")}:${String(
        snapMinutes
    ).padStart(2, "0")}`;

    // Update date if changed
    if (dateStr !== selectedSlots.value.date) selectedSlots.value.date = dateStr;

    if (!props.therapist.offers_after_hours) {
        const dt = DateTime.fromISO(`${dateStr}T${newTime}`, {
            zone: props.location?.timezone || "UTC",
        });

        const workStart = dt.set({
            hour: currentDateWorkhours.value.start,
            minute: 0,
        });
        const workEnd = dt.set({ hour: currentDateWorkhours.value.end, minute: 0 });

        if (dt < workStart) {
            selectedSlots.value.endTime = workStart.toFormat("HH:mm");
            return;
        }
        if (dt > workEnd) {
            selectedSlots.value.endTime = workEnd.toFormat("HH:mm");
            return;
        }
    }

    selectedSlots.value.endTime = newTime;
}

watch([ () => viewDate.value, () => currentView.value ], () => {
    selectedSlots.value = null;
    fetchEvents();
});

onMounted(async () => {
    isLoading.value = true;

    if (props.therapist.ROWID) {
        therapist_id.value = props.therapist.ROWID;
    } else {
        await axios
            .get(`${apiUrl}therapist`, {
                params: {
                    therapist_crm_id: props.therapist.zoho_crm_id,
                },
            })
            .then((res) => {
                therapist_id.value = res.data.ROWID;
            })
            .catch((err) => {
                console.error("Failed to fetch therapist ID:", new Error(err));
                loadingMessage.value =
          "Something went wrong. Please, try again, or contact support. (ERROR: THERAPIST_ID)";
                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: loadingMessage.value,
                });
            });
    }

    if (props.location.ROWID) {
        location_id.value = props.location.ROWID;
    } else {
        await axios
            .get(`${apiUrl}location`, {
                params: {
                    therapist_id: therapist_id.value,
                    location_crm_id: props.location.zoho_crm_id,
                },
            })
            .then((res) => {
                location_id.value = res.data.ROWID;
            })
            .catch((err) => {
                console.error("Failed to fetch location ID:", new Error(err));
                loadingMessage.value =
          "Something went wrong. Please, try again, or contact support. (ERROR: LOCATION_ID)";
                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: loadingMessage.value,
                });
            });
    }

    if (location_id.value && therapist_id.value) {
        fetchEvents().then(() => {
            isLoading.value = false;
        });

        if (!props.slotDuration) {
            document.addEventListener("mouseup", () => {
                if (isDragging.value) {
                    handleDragEnd();
                }
            });
        }

        // Initial date adjustment for booking/hold modes
        if (
            currentView.value === "day" &&
      [ "booking", "hold", "recommendation" ].includes(props.mode)
        ) {
            const nextAvailable = findNextAvailableDay(viewDate.value);
            if (!nextAvailable.equals(viewDate.value)) {
                viewDate.value = nextAvailable;
                currentDate.value = nextAvailable;
                Swal.fire({
                    title: "Date Adjusted",
                    text: `Moved to ${formatDate(
                        nextAvailable
                    )} as it's the next available date for bookings`,
                    icon: "info",
                });
            }
        }
    }
});

function handleSuggestionClick(suggestion) {
    emit("suggestion-click", {
        suggestion: suggestion,
        therapist_id: therapist_id.value,
        location_id: location_id.value,
    });
}

function handleVirtualClick(event) {
    emit("virtual-delete", {
        event: event,
        therapist_id: therapist_id.value,
        location_id: location_id.value,
    });
}

function formatDateTimeForInput(timeStr) {
    if (!selectedSlots.value?.date || !timeStr) return "";
    return `${selectedSlots.value.date}T${padTimeComponent(timeStr)}`;
}

defineExpose({
    changeView: changeView,
    handleDragStart: handleDragStart,
    handleDragEnd: handleDragEnd,
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-white rounded-lg shadow">
    <!-- Calendar Header -->
    <div class="p-1 h-32 border-b sticky top-0 z-20 bg-white">
      <div class="flex items-center justify-between mb-4">
        <!-- View Controls -->
        <div class="flex items-center">
          <button
            v-for="(label, type) in viewTypes"
            :key="type"
            class="px-3 py-1 transition-colors"
            :class="{
              'bg-blue-500 text-white': currentView === type,
              'bg-gray-100 hover:bg-gray-200': currentView !== type,
              'rounded-l': Object.keys(viewTypes).indexOf(type) === 0,
              'rounded-r':
                Object.keys(viewTypes).indexOf(type) ===
                Object.keys(viewTypes).length - 1,
            }"
            @click="changeView(type)"
          >
            {{ label }}
          </button>
        </div>

        <!-- Date Navigation -->
        <div class="flex items-center space-x-4">
          <button
            @click="navigateDate(-1)"
            class="p-2 rounded hover:bg-gray-100"
          >
            ◀
          </button>

          <span class="font-medium">
            {{ formatDate(viewDate) }}
          </span>

          <button
            @click="navigateDate(1)"
            class="p-2 rounded hover:bg-gray-100"
          >
            ▶
          </button>
        </div>
      </div>

      <!-- Working Hours / Toggles -->
      <div class="flex items-center justify-between">
        <div class="text-sm text-gray-500">
          Working Hours:

          <div v-if="currentDateWorkhours?.start?.hours !== null">
            {{ currentDateWorkhours.start.hours }}:{{
              ("" + currentDateWorkhours.start.minutes).padStart(2, "0")
            }}
            -
            {{ currentDateWorkhours.end.hours }}:{{
              ("" + currentDateWorkhours.end.minutes).padStart(2, "0")
            }}

            <span
              v-if="!therapist.offers_after_hours"
              class="text-sm text-red-500 select-none"
            >
              No after hours!
            </span>
          </div>

          <div v-else class="text-red-500">Not working today</div>

          (timezone: {{ location?.timezone || "Loading..." }})
        </div>

        <div class="flex space-x-4 text-sm select-none">
          <label
            class="flex items-center p-1 rounded-lg cursor-pointer hover:bg-gray-100"
          >
            <input type="checkbox" v-model="showOtherLocations" class="mr-1" />

            <span>Show other locations' events</span>
          </label>

          <label
            class="flex items-center p-1 rounded-lg cursor-pointer hover:bg-gray-100"
          >
            <input
              type="checkbox"
              id="toggle-suggestions"
              v-model="showSuggestions"
              class="mr-1"
            />

            <span> Suggestions ({{ suggestedSlots.length }}) </span>
          </label>

          <label
            class="flex items-center p-1 rounded-lg cursor-pointer hover:bg-gray-100"
          >
            <input
              type="checkbox"
              id="toggle-virtual"
              v-model="showVirtualEvents"
              class="mr-1"
            />

            <span> Virtual Events ({{ virtualEvents.length }}) </span>
          </label>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center select-none flex-1"
    >
      {{ loadingMessage }}
    </div>

    <!-- Calendar -->
    <div v-else class="flex-1">
      <div v-if="currentView === 'day'" class="min-h-full">
        <!-- Early Hours -->
        <div class="w-full">
          <button
            @click="toggleRange(HIDDEN_RANGES.early.id)"
            class="w-full px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
          >
            {{ HIDDEN_RANGES.early.label }}

            {{ collapsedOpen[HIDDEN_RANGES.early.id] ? "▾" : "▴" }}

            <span class="ml-2 text-xs text-gray-600">
              ({{ countEventsInRange(viewDate, HIDDEN_RANGES.early) }})
            </span>
          </button>
        </div>

        <!-- Day Grid -->
        <div
          class="calendar-interaction-column relative overflow-hidden select-none"
          @pointermove="handleCalendarPointerMove($event, viewDate)"
          @pointerdown.prevent="handleCalendarPointerDown($event, viewDate)"
          @pointerup.prevent="handleCalendarPointerUp($event, viewDate)"
          @pointercancel="handleCalendarLeave"
          @mouseleave="handleCalendarLeave"
        >
          <!-- Hour backgrounds -->
          <template
            v-for="segment in visibleSegments"
            :key="`day-${segment.hour}`"
          >
            <div
              v-if="segment.type === 'hour'"
              class="h-[60px] border-b relative"
              :class="hourBackgroundClass(segment.hour, viewDate)"
            >
              <div
                class="absolute top-0 left-0 w-16 pl-2 text-xs text-gray-500 pointer-events-none select-none"
              >
                {{ formatTime(segment.hour, 0) }}
              </div>

              <!-- 5-minute guide lines are CSS only -->
              <div
                class="calendar-five-minute-lines absolute inset-0 pointer-events-none"
              />
            </div>
          </template>

          <!-- Hover / drag preview -->
          <div
            v-if="hoverPreview && hoverPreview.date === viewDate.toISODate()"
            class="calendar-selection-preview absolute left-0 right-0 z-[5] pointer-events-none"
            :class="previewClass()"
            :style="previewStyleForDate(viewDate)"
          >
            <div class="px-2 text-xs font-medium">
              {{
                formatTime(
                  hoverPreview.startTime.split(":")[0],
                  hoverPreview.startTime.split(":")[1]
                )
              }}
              -
              {{
                formatTime(
                  hoverPreview.endTime.split(":")[0],
                  hoverPreview.endTime.split(":")[1]
                )
              }}
            </div>
          </div>

          <!-- Events -->
          <div
            class="absolute inset-0 pointer-events-none overflow-hidden z-10"
          >
            <template
              v-for="event in getEventsForDate(viewDate)"
              :key="getEventKey(event)"
            >
              <!-- Virtual -->
              <div
                v-if="event.isVirtual"
                data-calendar-event
                class="absolute w-full px-1 transition-colors border-2 border-dashed cursor-pointer pointer-events-auto overflow-hidden"
                :class="{
                  'bg-indigo-50 hover:bg-red-100 border-indigo-300':
                    !event.isSuggestion,
                  'bg-purple-50 hover:bg-green-100 border-purple-300':
                    event.isSuggestion,
                }"
                :style="getEventStyle(event)"
                @pointerenter="handleEventPointerEnter"
                @pointerdown.stop
                @click.stop="
                  event.isSuggestion
                    ? handleSuggestionClick(event)
                    : event.id && event.deleteable
                    ? emit('virtual-delete', {
                        event,
                        therapist_id,
                        location_id,
                      })
                    : null
                "
              >
                <div
                  class="text-xs font-medium"
                  :class="{
                    'text-indigo-700': !event.isSuggestion,
                    'text-purple-700': event.isSuggestion,
                  }"
                >
                  {{ event.time_start.toLocaleString(DateTime.TIME_SIMPLE) }}
                </div>

                <div class="text-sm truncate">
                  {{ event.label }}
                </div>

                <div
                  class="text-xs font-medium"
                  :class="{
                    'text-indigo-700': !event.isSuggestion,
                    'text-purple-700': event.isSuggestion,
                  }"
                >
                  {{ event.time_end.toLocaleString(DateTime.TIME_SIMPLE) }}
                </div>
              </div>

              <!-- Real event -->
              <div
                v-else-if="
                  event.type !== 'hart_hours' || props.mode === 'hart_hours'
                "
                data-calendar-event
                class="absolute w-full px-1 py-0.5 cursor-pointer transition-colors pointer-events-auto overflow-hidden"
                :class="{
                  'bg-red-100 hover:bg-red-200': event.type === 'absent',

                  'bg-yellow-100 hover:bg-yellow-200': event.type === 'hold',

                  'bg-blue-100 hover:bg-blue-200': event.type === 'booking',

                  'bg-green-100 hover:bg-green-200':
                    event.type === 'hart_hours',

                  'border-l-4 border-blue-500 shadow-sm opacity-75 hover:opacity-100':
                    event._isOtherLocation,
                }"
                :style="getEventStyle(event)"
                @pointerdown.stop
                @pointerenter="handleEventPointerEnter"
                @click.stop="handleSlotClick(viewDate, event)"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-sm font-medium">
                      {{ getEventLabel(event) }}
                    </span>

                    <span class="text-xs text-gray-600">
                      {{
                        event.time_start.toLocaleString(DateTime.TIME_SIMPLE)
                      }}

                      {{
                        !event.time_start.hasSame(viewDate, "day")
                          ? "(starts before today)"
                          : ""
                      }}

                      -

                      {{ event.time_end.toLocaleString(DateTime.TIME_SIMPLE) }}

                      {{
                        !event.time_end.hasSame(viewDate, "day")
                          ? "(ends after today)"
                          : ""
                      }}
                    </span>
                  </div>

                  <div class="flex items-center space-x-2 p-1">
                    <span
                      v-if="event._isOtherLocation"
                      class="px-2 py-0.5 rounded-lg text-xs font-semibold bg-blue-200 text-blue-800 border border-blue-400"
                    >
                      {{ event.location_name || "Other Location" }}
                    </span>

                    <span
                      v-if="event._isGlobal"
                      class="px-1 py-0.5 rounded-lg whitespace-nowrap text-xs font-semibold bg-purple-200 text-purple-800 border border-purple-400"
                    >
                      All Locations
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Late Hours -->
        <div class="w-full">
          <button
            @click="toggleRange(HIDDEN_RANGES.late.id)"
            class="w-full px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
          >
            {{ HIDDEN_RANGES.late.label }}

            {{ collapsedOpen[HIDDEN_RANGES.late.id] ? "▾" : "▴" }}

            <span class="ml-2 text-xs text-gray-600">
              ({{ countEventsInRange(viewDate, HIDDEN_RANGES.late) }})
            </span>
          </button>
        </div>
      </div>

      <!-- ===================================================== -->
      <!-- WEEK VIEW -->
      <!-- ===================================================== -->

      <div v-else-if="currentView === 'week'" class="flex flex-col h-full">
        <!-- Headers -->
        <div
          class="grid grid-cols-7 gap-1 p-2 border-b pl-14 bg-gray-50 sticky top-[7.82rem] z-20"
        >
          <h2
            v-for="date in getWeekDates()"
            :key="date.toISODate()"
            class="p-2 text-sm text-center rounded-lg cursor-pointer hover:bg-gray-200"
            :class="{
              'bg-blue-50 font-medium': isToday(date),
              'border-2 border-red-500': isToday(date),
            }"
            @click="changeView('day', date)"
          >
            {{ formatDate(date, "short") }}
          </h2>
        </div>

        <!-- Early Hours -->
        <div class="w-full">
          <button
            @click="toggleRange(HIDDEN_RANGES.early.id)"
            class="w-full px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
          >
            {{ HIDDEN_RANGES.early.label }}

            {{ collapsedOpen[HIDDEN_RANGES.early.id] ? "▾" : "▴" }}

            <span class="ml-2 text-xs text-gray-600">
              ({{
                getWeekDates().reduce(
                  (total, date) =>
                    total + countEventsInRange(date, HIDDEN_RANGES.early),
                  0
                )
              }})
            </span>
          </button>
        </div>

        <div class="flex flex-1">
          <!-- Times -->
          <div class="w-14 shrink-0">
            <div
              v-for="segment in visibleSegments"
              :key="`week-label-${segment.hour}`"
              class="text-xs text-center text-gray-600 h-[60px] text-nowrap border-b"
            >
              <div
                v-if="segment.type === 'hour'"
                class="h-[60px] flex items-center justify-center"
              >
                {{ formatTime(segment.hour, 0) }}
              </div>
            </div>
          </div>

          <!-- 7 day columns -->
          <div class="grid flex-1 grid-cols-7">
            <div
              v-for="date in getWeekDates()"
              :key="date.toISODate()"
              class="calendar-interaction-column border relative overflow-hidden select-none"
              :class="{
                'border-3 border-red-500': isToday(date),
              }"
              @pointermove="handleCalendarPointerMove($event, date)"
              @pointerdown.prevent="handleCalendarPointerDown($event, date)"
              @pointerup.prevent="handleCalendarPointerUp($event, date)"
              @pointercancel="handleCalendarLeave"
              @mouseleave="handleCalendarLeave"
            >
              <!-- Only one DOM element per hour now -->
              <div
                v-for="segment in visibleSegments"
                :key="`week-${date.toISODate()}-${segment.hour}`"
                class="h-[60px] border-b relative"
                :class="hourBackgroundClass(segment.hour, date)"
              >
                <div
                  class="calendar-five-minute-lines absolute inset-0 pointer-events-none"
                />
              </div>

              <!-- Single moving preview -->
              <div
                v-if="hoverPreview && hoverPreview.date === date.toISODate()"
                class="calendar-selection-preview absolute left-0 right-0 z-[5] pointer-events-none"
                :class="previewClass()"
                :style="previewStyleForDate(date)"
              >
                <div class="px-1 text-[0.65rem] font-medium truncate">
                  {{ hoverPreview.startTime }}
                  -
                  {{ hoverPreview.endTime }}
                </div>
              </div>

              <!-- Event layer -->
              <div
                class="absolute inset-0 pointer-events-none overflow-hidden z-10"
              >
                <template
                  v-for="event in getEventsForDate(date)"
                  :key="getEventKey(event, date)"
                >
                  <div
                    v-if="
                      event.type !== 'hart_hours' || props.mode === 'hart_hours'
                    "
                    data-calendar-event
                    class="absolute w-full px-1 cursor-pointer transition-colors pointer-events-auto overflow-hidden"
                    :class="{
                      'bg-red-100 hover:bg-red-200': event.type === 'absent',

                      'bg-yellow-100 hover:bg-yellow-200':
                        event.type === 'hold',

                      'bg-blue-100 hover:bg-blue-200': event.type === 'booking',

                      'bg-green-100 hover:bg-green-200':
                        event.type === 'hart_hours',

                      'border-l-4 border-blue-500 shadow-sm opacity-75 hover:opacity-100':
                        event._isOtherLocation,

                      'border-2 border-dashed bg-indigo-50 opacity-75 cursor-pointer hover:bg-red-100 border-indigo-300':
                        event.isVirtual && !event.isSuggestion,

                      'border-2 border-dashed bg-purple-50 opacity-75 cursor-pointer hover:bg-green-100 border-purple-300':
                        event.isVirtual && event.isSuggestion,
                    }"
                    :style="getWeekEventStyle(event, date)"
                    @pointerenter="handleEventPointerEnter"
                    @pointerdown.stop
                    @click.stop="
                      event.isVirtual && event.isSuggestion
                        ? handleSuggestionClick(event)
                        : event.isVirtual
                        ? handleVirtualClick(event)
                        : handleSlotClick(date, event)
                    "
                  >
                    <div class="flex flex-col py-1">
                      <span
                        v-if="event._isOtherLocation"
                        class="px-1 py-0.5 rounded text-[0.65rem] font-semibold bg-blue-200 text-blue-800 border border-blue-400 truncate"
                      >
                        {{ event.location_name || "Other Location" }}
                      </span>

                      <span
                        v-if="event._isGlobal"
                        class="px-1 py-0.5 rounded whitespace-nowrap text-[0.65rem] font-semibold
                        bg-purple-200 text-purple-800 border border-purple-400 truncate"
                      >
                        All Locations
                      </span>
                    </div>

                    <div>
                      <div class="text-[0.7rem] font-medium truncate">
                        {{
                          event.time_start.toLocaleString(DateTime.TIME_SIMPLE)
                        }}
                      </div>

                      <div class="text-[0.7rem] truncate">
                        {{ event.label || getEventLabel(event) }}
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Late Hours -->
        <div class="w-full">
          <button
            @click="toggleRange(HIDDEN_RANGES.late.id)"
            class="w-full px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
          >
            {{ HIDDEN_RANGES.late.label }}

            {{ collapsedOpen[HIDDEN_RANGES.late.id] ? "▾" : "▴" }}

            <span class="ml-2 text-xs text-gray-600">
              ({{
                getWeekDates().reduce(
                  (total, date) =>
                    total + countEventsInRange(date, HIDDEN_RANGES.late),
                  0
                )
              }})
            </span>
          </button>
        </div>
      </div>

      <!-- ===================================================== -->
      <!-- MONTH VIEW -->
      <!-- ===================================================== -->

      <div v-else class="grid grid-cols-7 overflow-y-auto gap-1 p-2">
        <div
          v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
          :key="day"
          class="p-2 font-medium text-center sticky top-0 z-20 bg-white"
        >
          {{ day }}
        </div>

        <div
          v-for="date in getMonthDates()"
          :key="date.toISODate()"
          class="border rounded min-h-[120px] cursor-pointer hover:bg-gray-200"
          :class="{
            'bg-gray-300': !date.hasSame(viewDate, 'month'),
            'border-red-500 border-2': isToday(date),
            'bg-gray-100': isPast(date),
          }"
          @click="changeView('day', date)"
        >
          <div class="p-2 text-sm border-b bg-gray-50">
            {{ formatDate(date, "short") }}
          </div>

          <div class="p-1 overflow-y-auto max-h-[100px]">
            <div
              v-for="event in getEventsForDate(date).filter(
                (e) => !e.isVirtual
              )"
              :key="getEventKey(event, date)"
              class="p-1 mb-1 text-xs rounded cursor-pointer"
              :class="{
                'bg-red-100': event.type === 'absent',
                'bg-yellow-100': event.type === 'hold',
                'bg-blue-100': event.type === 'booking',
              }"
              @click.stop="handleSlotClick(date, event)"
            >
              {{ getEventLabel(event) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- SLOT DIALOG -->
    <!-- ===================================================== -->

    <div
      v-if="showSlotDialog"
      class="slot-creation-dialog fixed inset-y-0 right-0 w-[30rem] bg-white shadow-lg transform transition-transform duration-300 z-[999]"
      :class="showSlotDialog ? 'translate-x-0' : 'translate-x-full'"
    >
      <div class="flex items-center justify-between p-4 border-b">
        <h2 class="text-lg font-semibold">
          {{
            isEditing
              ? `Edit ${editingEvent.type} Slot`
              : `Create ${eventData[props.mode].label} Slot`
          }}

          {{ isEditing && hasEditChanges ? "*" : "" }}
        </h2>

        <button
          @click="showSlotDialog = false; hoverPreview = null;"
          class="text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>

      <div class="p-4">
        <!-- Time -->
        <div
          class="grid gap-4 mb-4"
          :class="
            props.slotDuration ||
            (editingEvent && eventData[editingEvent.type].forceSlotDuration)
              ? 'grid-cols-1'
              : 'grid-cols-2'
          "
        >
          <div>
            <label class="block text-sm font-medium text-gray-700">
              Start Time
            </label>

            <input
              type="datetime-local"
              :value="formatDateTimeForInput(selectedSlots.startTime)"
              @input="handleStartTimeChange"
              :min="`${selectedSlots.date}T00:00`"
              step="300"
              class="block w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>

          <div
            v-if="
              !props.slotDuration ||
              (editingEvent && !eventData[editingEvent.type].forceSlotDuration)
            "
          >
            <label class="block text-sm font-medium text-gray-700">
              End Time
            </label>

            <input
              type="datetime-local"
              :value="formatDateTimeForInput(selectedSlots.endTime)"
              @input="handleEndTimeChange"
              :min="formatDateTimeForInput(selectedSlots.endTime)"
              step="300"
              class="block w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>
        </div>

        <!-- Presets -->
        <div
          v-if="
            !props.slotDuration ||
            (editingEvent && !eventData[editingEvent.type].forceSlotDuration)
          "
          class="grid gap-4 grid-cols-2 mb-4"
        >
          <button
            type="button"
            class="button-primary"
            @click="
              () => {
                const start = DateTime.fromISO(`${selectedSlots.date}T00:00`, {
                  zone: location?.timezone || 'UTC',
                });

                selectedSlots.startTime = start
                  .set({
                    hour: currentDateWorkhours.start.hours,
                    minute: currentDateWorkhours.start.minutes || 0,
                  })
                  .toFormat('HH:mm');

                selectedSlots.endTime = start
                  .set({
                    hour: currentDateWorkhours.end.hours,
                    minute: currentDateWorkhours.end.minutes || 0,
                  })
                  .toFormat('HH:mm');
              }
            "
          >
            Work Hours
          </button>

          <button
            type="button"
            class="button-primary"
            @click="
              () => {
                selectedSlots.startTime = '00:00';
                selectedSlots.endTime = '23:59';
              }
            "
          >
            Whole Day
          </button>
        </div>

        <!-- Validation -->
        <div
          v-if="!slotValidation.isValid"
          class="p-3 mb-4 text-sm border border-red-200 rounded bg-red-50"
        >
          <h4
            v-for="error in slotValidation.errors"
            :key="error"
            class="text-red-600 cursor-pointer"
            @click="handleCollisionClick"
          >
            ⚠️ {{ error }}
          </h4>
        </div>

        <!-- Scope -->
        <div
          v-if="['absent', 'hart_hours'].includes(props.mode)"
          class="flex items-center justify-between mb-4 p-3 border rounded"
        >
          <span class="text-sm font-medium"> Event scope </span>

          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="isGlobalEvent" />

            <span class="text-sm"> Global (all locations) </span>
          </label>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-2">
          <button
            v-if="isEditing"
            @click="handleDelete()"
            class="px-4 py-2 text-white bg-red-500 rounded hover:bg-red-600"
          >
            Delete
          </button>

          <button
            @click="
              selectedSlots = null;
              showSlotDialog = false;
              editingEvent = null;
              originalEventData = null;
              hoverPreview = null;
            "
            class="button"
          >
            Cancel
          </button>

          <button
            @click="handleSave()"
            :disabled="
              (!slotValidation.isValid && !slotValidation.ignorable) ||
              (isEditing && !hasEditChanges)
            "
            class="button-primary"
          >
            {{ isEditing ? "Update" : "Create" }}
            {{ slotValidation.ignorable ? "Anyway" : "" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.calendar-interaction-column {
  overflow: hidden;
  touch-action: none;
}

.calendar-five-minute-lines {
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent 4px,
    rgba(0, 0, 0, 0.035) 4px,
    rgba(0, 0, 0, 0.035) 5px
  );
}

.calendar-selection-preview {
  min-height: 5px;
  pointer-events: none;
  overflow: hidden;
  z-index: 5;
  opacity: 0.65;
  will-change: top, height;
}

.drag-slot {
  position: absolute;
  left: 0;
  right: 0;
  background: rgba(59, 130, 246, 0.1);
  pointer-events: none;
  z-index: 10;
}

.select-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.2;
  }
}

@media (max-width: 640px) {
  .slot-creation-dialog {
    position: fixed;
    inset: 0;
    margin: 0;
    border-radius: 0;
    max-width: none;
    width: 100%;
  }
}

.button {
  @apply px-4 py-2 text-gray-600 hover:bg-gray-100 rounded disabled:cursor-not-allowed;
}

.button-primary {
  @apply button;
  @apply bg-blue-500 disabled:bg-blue-300 text-white hover:bg-blue-600;
}
</style>
